import React, { useState, useEffect } from 'react';
import './Fichearticle.css';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend } from 'chart.js';
import DataLabelsPlugin from 'chartjs-plugin-datalabels';
import annotationPlugin from 'chartjs-plugin-annotation';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, annotationPlugin);
ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend, DataLabelsPlugin);

const API = window.location.hostname === 'localhost'
  ? 'http://localhost:5000/api'
  : 'https://myssa1.osc-fr1.scalingo.io/api';

const TYPES_AUTORISES = ['commande frn', 'commande ferme', 'prévision'];

// ─── Helpers granularité ──────────────────────────────────────────────────────
const getWeekKey = (d) => {
  const tmp = new Date(d);
  tmp.setHours(0, 0, 0, 0);
  const day = tmp.getDay() || 7;
  tmp.setDate(tmp.getDate() - day + 1);
  const startOfYear = new Date(tmp.getFullYear(), 0, 1);
  const weekNum = Math.ceil(((tmp - startOfYear) / 86400000 + 1) / 7);
  return `${tmp.getFullYear()}-S${String(weekNum).padStart(2, '00')}`;
};

const getMonthKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

const aggregateRows = (rows, granularity) => {
  if (granularity === 'jour') return rows;
  const groups = {};
  rows.forEach((r) => {
    const d = new Date(r['date']);
    if (isNaN(d)) return;
    const key = granularity === 'semaine' ? getWeekKey(d) : getMonthKey(d);
    if (!groups[key]) {
      groups[key] = { lastRow: null, sumDepart: 0, sumLivraison: 0, sumPrevision: 0, sumCommandeFerme: 0 };
    }
    const g = groups[key];
    if (!g.lastRow || new Date(r['date']) > new Date(g.lastRow['date'])) g.lastRow = r;
    const lf = parseFloat(r['livraison fournisseur']);
    if (!isNaN(lf)) g.sumLivraison += lf;
    const dc = parseFloat(r['depart client']);
    if (!isNaN(dc)) {
      g.sumDepart += dc;
      if (r['type de donnee'] === 'prévision')      g.sumPrevision     += dc;
      if (r['type de donnee'] === 'commande ferme') g.sumCommandeFerme += dc;
    }
  });
  return Object.values(groups)
    .filter(g => g.lastRow)
    .map(g => ({
      ...g.lastRow,
      'depart client':         g.sumDepart,
      'livraison fournisseur': g.sumLivraison,
      _sumPrevision:           g.sumPrevision,
      _sumCommandeFerme:       g.sumCommandeFerme,
    }))
    .sort((a, b) => new Date(a['date']) - new Date(b['date']));
};

const formatLabelGran = (dateStr, granularity) => {
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr || '';
  if (granularity === 'jour')
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  if (granularity === 'semaine') {
    const startOfYear = new Date(d.getFullYear(), 0, 1);
    const weekNum = Math.ceil(((d - startOfYear) / 86400000 + 1) / 7);
    return `S${String(weekNum).padStart(2, '0')} ${d.getFullYear()}`;
  }
  return d.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
};

const Fichearticle = ({ itemPrefill }) => {
  const [inputValue, setInputValue]                                   = useState('');
  const [description, setDescription]                                 = useState('');
  const [delaisApprovisionnement, setDelaisApprovisionnement]         = useState('');
  const [delaisApprovisionnementReel, setDelaisApprovisionnementReel] = useState('');
  const [stockminipropose, setstockminipropose]                       = useState('150');
  const [stockMini, setStockMini]                                     = useState('');
  const [stockMain, setStockMain]                                     = useState(0);
  const [dateLivraison, setDateLivraison]                             = useState('');
  const [totalLivraisonFournisseur, setTotalLivraisonFournisseur]     = useState(0);
  const [totalCommandeClient, setTotalCommandeClient]                 = useState(0);
  const [totalPrevision, setTotalPrevision]                           = useState(0);
  const [totalBesoins, setTotalBesoins]                               = useState(0);
  const [data, setData]                                               = useState([]);
  const [ruptureSeche, setRuptureSeche]                               = useState('');
  const [graphData, setGraphData]                                     = useState(null);
  const [filteredData, setFilteredData]                               = useState([]);
  const [overridesLivraison, setOverridesLivraison]                   = useState({});
  const [overridesPrevision, setOverridesPrevision]                   = useState({});
  const [granularity, setGranularity]                                 = useState('jour');

  useEffect(() => {                                    // ← ajouter ici
    document.title = 'MYSSA – Fiche article';
  }, []);

  // ── Autocomplete ─────────────────────────────────────────────
  const [allItems, setAllItems]               = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  // ─────────────────────────────────────────────────────────────

  const handleInputChange = (event) => {
    setInputValue(event.target.value);
    setShowSuggestions(true);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${API}/donnee_histogramme`);
        if (!response.ok) throw new Error(`Erreur serveur : ${response.status}`);
        const jsonData = await response.json();
        setData(jsonData);
        // Extraire items uniques triés
        const items = [...new Set(jsonData.map(d => d['Item']).filter(Boolean))].sort();
        setAllItems(items);
      } catch (error) {
        console.error('Erreur lors du chargement des données JSON:', error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (itemPrefill) setInputValue(itemPrefill);
  }, [itemPrefill]);

  useEffect(() => {
    if (itemPrefill && data.length > 0 && inputValue === itemPrefill) {
      lancerRecherche(itemPrefill, data);
    }
  }, [data, inputValue, itemPrefill]);

  useEffect(() => {
    if (filteredData.length > 0) {
      const stockMiniValue = parseFloat(filteredData[0]['stock mini'] || 0);
      setGraphData(buildChartData(filteredData, stockMiniValue, overridesLivraison, overridesPrevision, granularity));
    }
  }, [granularity]);

  const buildChartData = (filtered, stockMiniValue, currentOverridesLivraison, currentOverridesPrevision, gran = 'jour') => {
    const tableauFiltre = filtered.filter(item => TYPES_AUTORISES.includes(item['type de donnee']));
    const stockDepart   = parseFloat(filtered[0]?.stock || 0);
    let stock = stockDepart;
    const stockSimuleMap = new Map();
    tableauFiltre.forEach((item, idx) => {
      const livraisonOrigine = parseFloat(item['livraison fournisseur']) || 0;
      const livraisonSimulee = currentOverridesLivraison[idx] !== undefined
        ? Number(currentOverridesLivraison[idx]) : livraisonOrigine;
      let besoin;
      if (item['type de donnee'] === 'prévision') {
        besoin = currentOverridesPrevision[idx] !== undefined
          ? Number(currentOverridesPrevision[idx])
          : (parseFloat(item['depart client']) || 0);
      } else {
        besoin = parseFloat(item['depart client']) || 0;
      }
      stock = stock + livraisonSimulee - besoin;
      stockSimuleMap.set(item['date'], Math.round(stock));
    });

    const hasOverride =
      Object.keys(currentOverridesLivraison).length > 0 ||
      Object.keys(currentOverridesPrevision).length > 0;

    const aggFiltered      = aggregateRows(filtered, gran);
    const aggTableauFiltre = aggregateRows(tableauFiltre, gran);

    return {
      labels: aggFiltered.map(item => formatLabelGran(item['date'], gran)),
      datasets: [
        {
          label: 'Stock projeté',
          data: aggFiltered.map(item => Math.round(parseFloat(item['stock projete']) || 0)),
          backgroundColor: 'rgba(8,9,9,0.5)', borderColor: 'rgb(7,8,8)',
          borderWidth: 2, fill: false, type: 'line',
          datalabels: { display: false },
        },
        ...(hasOverride ? [{
          label: 'Stock projeté simulé',
          data: aggFiltered.map(item => stockSimuleMap.get(item['date']) ?? null),
          backgroundColor: 'rgba(56,189,248,0.15)', borderColor: 'rgba(56,189,248,1)',
          borderWidth: 2, borderDash: [6, 4],
          fill: false, type: 'line', spanGaps: true,
          datalabels: { display: false },
        }] : []),
        {
          label: 'Stock de sécurité système',
          data: new Array(aggFiltered.length).fill(Math.round(stockMiniValue)),
          backgroundColor: 'rgba(255,99,132,0.5)', borderColor: 'rgba(255,99,132,1)',
          borderWidth: 1, fill: false, type: 'line',
          datalabels: { display: false },
        },
        {
          label: 'Prévision',
          data: aggTableauFiltre.map(item =>
            gran === 'jour'
              ? (item['type de donnee'] === 'prévision' ? Math.round(parseFloat(item['depart client']) || 0) : 0)
              : Math.round(item._sumPrevision || 0)
          ),
          backgroundColor: 'rgba(54,162,235,0.5)', borderColor: 'rgba(54,162,235,1)',
          borderWidth: 1, fill: true, type: 'bar', stack: 'prevision',
          datalabels: {
            display: (ctx) => ctx.dataset.data[ctx.dataIndex] !== 0,
            color: 'black', anchor: 'end', align: 'top',
          },
        },
        {
          label: 'Commande ferme',
          data: aggTableauFiltre.map(item =>
            gran === 'jour'
              ? (item['type de donnee'] === 'commande ferme' ? Math.round(parseFloat(item['depart client']) || 0) : 0)
              : Math.round(item._sumCommandeFerme || 0)
          ),
          backgroundColor: 'rgba(255,99,132,0.5)', borderColor: 'rgba(255,99,132,1)',
          borderWidth: 1, fill: true, type: 'bar', stack: 'commande',
          datalabels: {
            display: (ctx) => ctx.dataset.data[ctx.dataIndex] !== 0,
            color: 'black', anchor: 'end', align: 'top',
          },
        },
        {
          label: 'Livraison fournisseur',
          data: aggTableauFiltre.map(item => Math.round(parseFloat(item['livraison fournisseur']) || 0)),
          backgroundColor: 'rgba(255,159,64,0.5)', borderColor: 'rgba(255,159,64,1)',
          borderWidth: 1, fill: true, type: 'bar', stack: 'livraison',
          datalabels: {
            display: (ctx) => ctx.dataset.data[ctx.dataIndex] !== 0,
            color: 'black', anchor: 'end', align: 'top',
          },
        },
      ],
    };
  };

  const handleOverrideLivraison = (idx, value) => {
    const next = { ...overridesLivraison, [idx]: value };
    setOverridesLivraison(next);
    if (filteredData.length > 0) {
      const stockMiniValue = parseFloat(filteredData[0]['stock mini'] || 0);
      setGraphData(buildChartData(filteredData, stockMiniValue, next, overridesPrevision, granularity));
    }
  };

  const handleOverridePrevision = (idx, value) => {
    const next = { ...overridesPrevision, [idx]: value };
    setOverridesPrevision(next);
    if (filteredData.length > 0) {
      const stockMiniValue = parseFloat(filteredData[0]['stock mini'] || 0);
      setGraphData(buildChartData(filteredData, stockMiniValue, overridesLivraison, next, granularity));
    }
  };

  const resetSimulation = () => {
    setOverridesLivraison({});
    setOverridesPrevision({});
    if (filteredData.length > 0) {
      const stockMiniValue = parseFloat(filteredData[0]['stock mini'] || 0);
      setGraphData(buildChartData(filteredData, stockMiniValue, {}, {}, granularity));
    }
  };

  const lancerRecherche = (valeur, sourceData) => {
    setOverridesLivraison({});
    setOverridesPrevision({});
    const filtered = sourceData
      .filter(item => item['Item'].trim().toLowerCase() === valeur.trim().toLowerCase())
      .sort((a, b) => new Date(a['date']) - new Date(b['date']));
    setFilteredData(filtered);
    if (filtered.length > 0) {
      setDescription(filtered[0].description);
      setDelaisApprovisionnement(filtered[0]['delais appro system'] || 'Délai non trouvé');
      setDelaisApprovisionnementReel(filtered[0]['delais appro reel'] ?? 'Délai non trouvé');
      setStockMini(filtered[0]['stock mini'] || '0');
      setStockMain(parseFloat(filtered[0].stock || 0));
      setDateLivraison(filtered[0]['prochaine date de livraison'] || 'Date non trouvée');
      setTotalLivraisonFournisseur(calculateTotalLivraisonFournisseur(filtered));
      setTotalCommandeClient(calculateTotalCommandeClient(filtered));
      setTotalPrevision(calculateTotalPrevision(filtered));
      setTotalBesoins(calculateTotalBesoins(filtered));
      checkRuptureSeche(filtered, valeur);
      const stockMiniValue = parseFloat(filtered[0]['stock mini'] || 0);
      setGraphData(buildChartData(filtered, stockMiniValue, {}, {}, granularity));
    } else {
      setDescription('Aucune correspondance trouvée');
      setDelaisApprovisionnement('Délai non trouvé');
      setDelaisApprovisionnementReel('Délai non trouvé');
      setStockMini('Stock non trouvé');
      setStockMain('Stock non trouvé');
      setDateLivraison('Date non trouvée');
      setTotalLivraisonFournisseur(0);
      setTotalCommandeClient(0);
      setTotalPrevision(0);
      setTotalBesoins(0);
      setRuptureSeche('');
      setGraphData(null);
    }
  };

  const checkRuptureSeche = (filteredData, valeur) => {
    for (const item of filteredData) {
      if (
        item['Item'] &&
        item['Item'].trim().toLowerCase() === valeur.trim().toLowerCase() &&
        item['rupture seche'] &&
        item['rupture seche'].toLowerCase().includes('rupture')
      ) { setRuptureSeche('oui'); return; }
    }
    setRuptureSeche('non');
  };

  const calculateTotalLivraisonFournisseur = (fd) =>
    Math.floor(fd.reduce((t, i) => t + (parseFloat(i['livraison fournisseur']) || 0), 0));
  const calculateTotalCommandeClient = (fd) =>
    Math.floor(fd.reduce((t, i) => i['type de donnee'] === 'commande ferme' ? t + (parseFloat(i['depart client']) || 0) : t, 0));
  const calculateTotalPrevision = (fd) =>
    Math.floor(fd.reduce((t, i) => i['type de donnee'] === 'prévision' ? t + (parseFloat(i['depart client']) || 0) : t, 0));
  const calculateTotalBesoins = (fd) =>
    Math.floor(fd.reduce((t, i) => t + (parseFloat(i['depart client']) || 0), 0));

  const formatDateShort = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d)) return value;
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  };

  const formatDateFull = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d)) return value;
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setShowSuggestions(false);
    if (inputValue && data.length > 0) lancerRecherche(inputValue, data);
  };

  const suggestionsFiltrees = allItems
    .filter(r => r.toLowerCase().includes(inputValue.toLowerCase()))
    .slice(0, 30);

  const stockProjetéValue = stockMain + totalLivraisonFournisseur - totalBesoins;

  const tableauInfo = [
    { label: 'Description',                           value: description },
    { label: "Délai d'approvisionnement",             value: isNaN(Number(delaisApprovisionnement)) ? delaisApprovisionnement : Math.round(Number(delaisApprovisionnement)) },
    { label: 'Délai réel',                            value: isNaN(Number(delaisApprovisionnementReel)) ? delaisApprovisionnementReel : Math.round(Number(delaisApprovisionnementReel)) },
    { label: 'Prochaine date de livraison possible',  value: formatDateFull(dateLivraison) },
    { label: 'Stock mini',                            value: Math.round(Number(stockMini)) || stockMini },
    { label: 'Stock en main',                         value: stockMain },
    { label: 'Total commande fournisseur',            value: totalLivraisonFournisseur },
    { label: 'Total commande client',                 value: totalCommandeClient },
    { label: 'Total prévision',                       value: totalPrevision },
    { label: 'Total besoins',                         value: totalBesoins },
    { label: 'Valeur stock projeté',                  value: stockProjetéValue, color: stockProjetéValue >= 0 ? '#15803d' : '#b91c1c' },
    { label: 'Rupture sèche',                         value: ruptureSeche, color: ruptureSeche === 'oui' ? '#b91c1c' : ruptureSeche === 'non' ? '#15803d' : 'inherit', bgColor: ruptureSeche === 'oui' ? '#fee2e2' : ruptureSeche === 'non' ? '#dcfce7' : 'transparent' },
  ];

  const chartOptions = {
    responsive: true,
    scales: {
      x: { stacked: false, ticks: { display: true, maxRotation: 45, minRotation: 0 } },
      y: { stacked: false, ticks: { display: true } },
    },
    plugins: {
      legend: { display: true },
      datalabels: { color: 'black', font: { weight: 'bold' } },
    },
    barPercentage: 0.8,
    categoryPercentage: 0.9,
  };

  const tableauData = filteredData.filter(item => TYPES_AUTORISES.includes(item['type de donnee']));

  const hasOverride =
    Object.keys(overridesLivraison).length > 0 ||
    Object.keys(overridesPrevision).length > 0;

  const stockSimuleTableau = (() => {
    if (!filteredData.length) return [];
    const stockDepart = parseFloat(filteredData[0]?.stock || 0);
    let stock = stockDepart;
    return tableauData.map((item, idx) => {
      const livraisonOrigine = parseFloat(item['livraison fournisseur']) || 0;
      const livraisonSimulee = overridesLivraison[idx] !== undefined ? Number(overridesLivraison[idx]) : livraisonOrigine;
      let besoin;
      if (item['type de donnee'] === 'prévision') {
        besoin = overridesPrevision[idx] !== undefined ? Number(overridesPrevision[idx]) : (parseFloat(item['depart client']) || 0);
      } else {
        besoin = parseFloat(item['depart client']) || 0;
      }
      stock = stock + livraisonSimulee - besoin;
      return Math.round(stock);
    });
  })();

  const inputStyle = (estModifie) => ({
    width: '100%',
    border: estModifie ? '1.5px solid #38bdf8' : '1px solid #e2e8f0',
    borderRadius: '4px', padding: '2px 4px', textAlign: 'center',
    fontSize: '11px',
    backgroundColor: estModifie ? '#f0f9ff' : 'white',
    color: estModifie ? '#0369a1' : 'inherit',
    fontWeight: estModifie ? '600' : 'normal',
    outline: 'none',
  });

  const LabelLigne = ({ label }) => (
    <td style={{ textAlign: 'center', fontWeight: '500', fontSize: '11px', whiteSpace: 'nowrap' }}>
      {label}
      {hasOverride && (
        <span title="Réinitialiser la simulation" onClick={resetSimulation}
          style={{ marginLeft: '6px', cursor: 'pointer', color: '#38bdf8', fontSize: '10px' }}>
          ↺
        </span>
      )}
    </td>
  );

  const GranBtn = ({ k, label }) => (
    <button
      onClick={() => setGranularity(k)}
      style={{
        padding: '4px 12px', borderRadius: 16, cursor: 'pointer', fontSize: 12,
        fontWeight: granularity === k ? 600 : 400,
        border: granularity === k ? '1.5px solid #185FA5' : '0.5px solid #D1D5DB',
        background: granularity === k ? '#185FA5' : '#FFFFFF',
        color: granularity === k ? '#FFFFFF' : '#374151',
        transition: 'all 0.15s',
      }}
    >
      {label}
    </button>
  );

  return (
    <div className="centered-container">

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', marginLeft: '-25px', marginTop: '-10px' }}>
        <div style={{ width: '3px', height: '18px', background: 'linear-gradient(180deg, #04202d, #38bdf8)', borderRadius: '2px' }} />
        <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '20px', fontWeight: '200', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#04202d' }}>
          Fiche article
        </span>
      </div>

      <div className="home-container">

    {/* ── Champ recherche avec autocomplete ── */}
<div className="fiche-container">
  <form className="fiche-form" onSubmit={handleSubmit}>
    <label htmlFor="articleInput"></label>
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        id="articleInput"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => setShowSuggestions(true)}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
        placeholder="Entrez votre article"
        autoComplete="off"
      />
      {showSuggestions && inputValue && suggestionsFiltrees.length > 0 && (
        <div style={{
          position: 'fixed',           // ← fixed au lieu de absolute
          zIndex: 99999,               // ← très haut pour passer au-dessus de tout
          background: '#fff',
          border: '1.5px solid #ddd',
          borderRadius: '7px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          maxHeight: 220,
          overflowY: 'auto',
          width: '250px',
          marginTop: '4px',
        }}>
          {suggestionsFiltrees.map((ref, i) => (
            <div
              key={i}
              onMouseDown={() => {
                setInputValue(ref);
                setShowSuggestions(false);
                lancerRecherche(ref, data);
              }}
              style={{
                padding: '8px 12px',
                fontSize: '13px',
                cursor: 'pointer',
                borderBottom: '1px solid #f0f0f0',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            >
              {ref}
            </div>
          ))}
        </div>
      )}
    </div>
    <button type="submit">Rechercher</button>
  </form>
</div>

        <table className="fiche-table">
          <tbody>
            <table style={{ position: 'absolute', top: '150px', left: '20px', width: '220px', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: '12px' }}>
              <tbody>
                {tableauInfo.map((row, index) => (
                  <tr key={index}>
                    <td style={{ width: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 'bold', border: '1px solid #f5f1eb', padding: '4px 8px', background: '#f9f7f4', fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px', color: '#8b7b64' }}>
                      {row.label}
                    </td>
                    <td style={{ width: '180px', whiteSpace: 'normal', overflow: 'hidden', textOverflow: 'ellipsis', border: '1px solid #f5f1eb', padding: '4px 8px', color: row.color || '#1e293b', backgroundColor: row.bgColor || 'transparent', fontFamily: "'IBM Plex Sans', sans-serif", fontSize: '12px' }}>
                      {row.value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </tbody>
        </table>

        {graphData && (
          <div className="chart-container" style={{ position: 'absolute', top: '-80px', left: '500px', overflow: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <GranBtn k="jour"    label="Jour" />
              <GranBtn k="semaine" label="Semaine" />
              <GranBtn k="mois"    label="Mois" />
            </div>
            <Line data={graphData} options={chartOptions} />
            <div className="table-container" style={{ position: 'absolute', top: '620px', left: '-7px', width: '1100px' }}>
              <table className="styled-table" style={{ width: '100%', tableLayout: 'fixed' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'center', width: '90px', fontSize: '10px' }}>—</th>
                    {tableauData.map((item, index) => (
                      <th key={index} style={{ textAlign: 'center', fontSize: '11px' }}>
                        {formatDateShort(item['date'])}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <LabelLigne label="Cmde frn" />
                    {tableauData.map((item, index) => {
                      const valOrigine  = Math.round(parseFloat(item['livraison fournisseur']) || 0);
                      const valAffichee = overridesLivraison[index] !== undefined ? overridesLivraison[index] : valOrigine;
                      const estModifie  = overridesLivraison[index] !== undefined && Number(overridesLivraison[index]) !== valOrigine;
                      return (
                        <td key={index} style={{ textAlign: 'center', padding: '2px' }}>
                          <input type="number" value={valAffichee} onChange={(e) => handleOverrideLivraison(index, e.target.value)} style={inputStyle(estModifie)} />
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <LabelLigne label="Prévision" />
                    {tableauData.map((item, index) => {
                      const valOrigine   = item['type de donnee'] === 'prévision' ? Math.round(parseFloat(item['depart client']) || 0) : 0;
                      const valAffichee  = overridesPrevision[index] !== undefined ? overridesPrevision[index] : valOrigine;
                      const estModifie   = overridesPrevision[index] !== undefined && Number(overridesPrevision[index]) !== valOrigine;
                      const estPrevision = item['type de donnee'] === 'prévision';
                      return (
                        <td key={index} style={{ textAlign: 'center', padding: '2px' }}>
                          {estPrevision ? (
                            <input type="number" value={valAffichee} onChange={(e) => handleOverridePrevision(index, e.target.value)} style={inputStyle(estModifie)} />
                          ) : (
                            <span style={{ fontSize: '11px' }}>0</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td style={{ textAlign: 'center', fontWeight: '500' }}>Cmde client</td>
                    {tableauData.map((item, index) => (
                      <td key={index} style={{ textAlign: 'center' }}>
                        {item['type de donnee'] === 'commande ferme' ? Math.round(item['depart client']) : 0}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td style={{ textAlign: 'center', fontWeight: '500', fontSize: '11px' }}>Stock projeté</td>
                    {tableauData.map((item, index) => (
                      <td key={index} style={{
                        backgroundColor: item['stock projete'] >= 0 ? '#dcfce7' : '#fee2e2',
                        color: item['stock projete'] >= 0 ? '#15803d' : '#b91c1c',
                        textAlign: 'center', fontWeight: '500',
                      }}>
                        {Math.round(item['stock projete'])}
                      </td>
                    ))}
                  </tr>
                  {hasOverride && (
                    <tr>
                      <td style={{ textAlign: 'center', fontWeight: '600', fontSize: '11px', color: '#0369a1', whiteSpace: 'nowrap' }}>Stock simulé</td>
                      {stockSimuleTableau.map((val, index) => (
                        <td key={index} style={{
                          backgroundColor: val >= 0 ? '#e0f2fe' : '#fee2e2',
                          color: val >= 0 ? '#0369a1' : '#b91c1c',
                          textAlign: 'center', fontWeight: '600',
                          border: '1.5px solid #38bdf8',
                        }}>
                          {val}
                        </td>
                      ))}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Fichearticle;